import { Radio, RadioGroup, RadioGroupProps } from "@chakra-ui/react";
import { upperFirst } from "lodash";
import {
  ForwardedRef,
  forwardRef,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { AssertionError } from "utils/assertions";

export function getStringValue<TOptions>(value: TOptions): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

type LabelsForOption<TOptions> = TOptions extends string | number | symbol
  ? Partial<Record<TOptions, ReactNode>>
  : never;

export type BaseRadioOptionsSelectProps<TOptions> = {
  getIsDisabled?: (option: TOptions) => boolean | undefined;
  getLabel?: (option: TOptions) => ReactNode;
  labels?: LabelsForOption<TOptions>;
  onChange: (option: TOptions) => void;
  options: TOptions[] | readonly TOptions[];
  value?: TOptions | null;
};

export function useRadioOptionsSelect<TOptions>(
  props: BaseRadioOptionsSelectProps<TOptions>,
) {
  const { getLabel, labels, options, onChange, value } = props;

  const fullOptions = useMemo(
    () =>
      options.map((option) => {
        const strValue = getStringValue(option);
        return {
          label: getLabel?.(option) || labels?.[option] || upperFirst(strValue),
          raw: option,
          value: strValue,
        };
      }),
    [options, labels, getLabel],
  );

  const handleChange = useCallback(
    (nextValue: string) => {
      const optVal = options.find((opt) => getStringValue(opt) === nextValue);
      if (optVal === undefined) {
        throw new AssertionError(
          `Could not get option for value: ${nextValue}`,
        );
      }
      onChange(optVal);
    },
    [options, onChange],
  );

  return {
    fullOptions,
    handleChange,
    strValue: getStringValue(value),
  };
}

export type RadioOptionsSelectProps<TOptions> = Omit<
  RadioGroupProps,
  "children" | "onChange" | "value"
> &
  BaseRadioOptionsSelectProps<TOptions>;

function RadioOptionsSelect<TOptions>(
  {
    getIsDisabled,
    getLabel,
    labels,
    onChange,
    options,
    value,
    ...rest
  }: RadioOptionsSelectProps<TOptions>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const { fullOptions, handleChange, strValue } = useRadioOptionsSelect({
    getLabel,
    labels,
    onChange,
    options,
    value,
  });
  return (
    <RadioGroup
      ref={ref}
      mx={-2}
      value={strValue}
      onChange={handleChange}
      {...rest}
    >
      {fullOptions.map((option) => (
        <Radio
          key={option.value}
          value={option.value}
          isDisabled={getIsDisabled?.(option.raw)}
          mx={2}
        >
          {option.label}
        </Radio>
      ))}
    </RadioGroup>
  );
}

export default forwardRef(RadioOptionsSelect);
